import { CollectionImage } from "@/app/collections/view/collection-image"
import { useIsMobileEmailUser } from "@/app/hooks/use-is-mobile-email-user"
import { getItemHref } from "@/app/navigation/item"
import type { NftMiniature } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import Link from "next/link"
import { useScanner } from "../anaconda-sdk-scanner"
import { AppToast, SuccessViewTransaction } from "./app-toast"
import { useToastError } from "./toast-util"

export const AcceptCollectionBidToast = {
  Error: function AcceptCollectionBidToastError({
    nft,
    id,
    error,
  }: {
    nft: NftMiniature
    id: string | number
    error: unknown
  }) {
    const { name, image } = nft
    const { message, type } = useToastError(error, nft.collection.blockchain)

    const title = getAcceptCollectionBidText({ nft })
    const main = <AcceptCollectionBidLink nft={nft} />

    if (type === "destructive") {
      return (
        <AppToast.Error
          id={id}
          title={title}
          image={<CollectionImage collectionName={name} imageSrc={image} />}
          main={main}
          second={
            <div className="font-semibold text-destructive">{message}</div>
          }
        />
      )
    } else if (type === "surface") {
      return (
        <AppToast.Warning
          id={id}
          image={<CollectionImage collectionName={name} imageSrc={image} />}
          title={title}
          main={main}
          second={
            <div className="font-semibold text-muted-foreground">{message}</div>
          }
        />
      )
    }
  },
  Pending: function CollectionBidToastPending({
    nfts,
    id,
  }: {
    nfts: NftMiniature[]
    id: string | number
  }) {
    const nft = nfts[0]
    const { name, image } = nft
    const isMobileEmailUser = useIsMobileEmailUser()

    if (isMobileEmailUser) {
      return null
    }

    return (
      <AppToast.Pending
        id={id}
        image={<CollectionImage collectionName={name} imageSrc={image} />}
        title={getAcceptCollectionBidText({ nft })}
        main={<AcceptCollectionBidLink nft={nft} />}
        second={
          <div className="font-semibold text-muted-foreground">
            <Trans>Pending...</Trans>
          </div>
        }
      />
    )
  },
  Success: function CollectionBidToastSuccess({
    id,
    nft,
    transactionId,
  }: {
    nft: NftMiniature
    id: string | number
    transactionId: string
  }) {
    const { name, image } = nft
    return (
      <AppToast.Success
        id={id}
        image={<CollectionImage collectionName={name} imageSrc={image} />}
        title={getAcceptCollectionBidText({ nft })}
        main={<AcceptCollectionBidLink nft={nft} />}
        second={
          <SuccessViewTransaction
            transactionId={transactionId}
            blockchain={nft.collection.blockchain}
          />
        }
      />
    )
  },
}

function AcceptCollectionBidLink({ nft }: { nft: NftMiniature }) {
  const { name, id } = nft
  const itemHref = getItemHref(id)
  return (
    <Trans>
      Accept collection bid{" "}
      <Link href={itemHref} className="text-brand">
        {name}
      </Link>{" "}
    </Trans>
  )
}

function getAcceptCollectionBidText({ nft }: { nft: NftMiniature }) {
  const { name } = nft
  const title = `Accept collection bid for ${name}`
  return title
}
